import Phaser from 'phaser';
import { PALETTE } from '../config';
import { AudioEngine } from '../game/AudioEngine';
import { CloudSync } from '../game/CloudSync';
import { SaveManager } from '../game/SaveManager';
import { setArenaPlay } from '../game/arenaContext';
import { arenaDetail, arenaJoin, formatTimeLeft, prizeShares, metricKindOf, type TournDetail } from '../game/arena';
import { formatMetric } from '../game/data/metricFormat';
import { t, disciplineLabel } from '../game/i18n';
import { addBackButton } from '../ui/BackButton';
import { Button } from '../ui/Button';
import { drawGradientBackground } from '../ui/Background';
import { drawPanel } from '../ui/Panel';
import { onResize, viewport } from '../ui/viewport';

/** Tournament detail: header + prize, disciplines to play (if joined & live), live standings. */
export class ArenaDetail extends Phaser.Scene {
  private tournamentId = '';
  private busy = false;
  private tv?: TournDetail;
  private selDisc: string | null = null;            // selected event → right panel shows its standings
  private colObjects: Phaser.GameObjects.GameObject[] = []; // everything in the two columns (re-rendered on select)
  private colTop = 0;
  private colH = 0;

  constructor() {
    super('ArenaDetail');
  }

  private track<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.colObjects.push(o);
    return o;
  }

  init(data: { tournamentId?: string }): void {
    if (data?.tournamentId) this.tournamentId = data.tournamentId;
  }

  create(): void {
    const { cx, h, s } = viewport(this);
    drawGradientBackground(this);
    addBackButton(this, s, () => this.scene.start('Arena'));
    const loading = this.add.text(cx, h * 0.5, t('arena.loading'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(18 * s)}px`, color: '#9fb0e0',
    }).setOrigin(0.5);
    void arenaDetail(this.tournamentId).then((r) => {
      loading.destroy();
      if (!r.ok || !r.data) { this.add.text(cx, h * 0.5, t('arena.offline'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(16 * s)}px`, color: '#ff8f9c' }).setOrigin(0.5); return; }
      SaveManager.instance.setServerCoins(r.data.coins);
      this.render(r.data.tournament, r.data.now);
    });
    const w0 = this.scale.width, h0 = this.scale.height;
    onResize(this, () => { if (Math.abs(this.scale.width - w0) > 2 || Math.abs(this.scale.height - h0) > 2) this.scene.restart({ tournamentId: this.tournamentId }); });
  }

  private render(tv: TournDetail, now: number): void {
    const { w, h, cx, s } = viewport(this);
    this.tv = tv; this.selDisc = null; this.colObjects = [];

    // ── Header ──
    const icon = tv.system ? '⭐ ' : (tv.visibility === 'private' ? '🔒 ' : '');
    this.add.text(cx, h * 0.08, `${icon}${tv.name}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(26 * s)}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 3, '#000000aa', 6);

    const st = tv.status === 'running' ? { txt: t('arena.statusRunning'), c: '#6ef0a0' }
      : tv.status === 'upcoming' ? { txt: t('arena.statusUpcoming'), c: '#59d2ff' }
      : { txt: t('arena.statusEnded'), c: '#9fb0e0' };
    const timeStr = tv.status === 'upcoming' ? formatTimeLeft(tv.startAt - now) : (tv.status === 'running' ? formatTimeLeft(tv.endAt - now) : '—');
    this.add.text(cx, h * 0.15, `${st.txt}  ·  ⏱ ${timeStr}  ·  👥 ${tv.entrantCount}  ·  🏆 ${tv.pool} 🪙`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(14 * s))}px`, color: '#cfd8f5',
    }).setOrigin(0.5);

    // Prize breakdown line.
    const shares = prizeShares(tv.entrantCount).map((sh, i) => `${i + 1}°: ${Math.floor(tv.pool * sh)}🪙`).join('   ');
    this.add.text(cx, h * 0.205, `${t('arena.prize')}: ${tv.pool > 0 ? shares : '—'}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(11, Math.round(13 * s))}px`, color: '#ffd24a',
    }).setOrigin(0.5);

    this.coinChip(w - 16 * s, 12 * s, s);

    // Creator can edit/cancel their own (non-system) tournament until it's settled.
    if (tv.mine && !tv.system && !tv.settled) {
      new Button(this, cx, h * 0.25, `✏️ ${t('arena.edit')}`, () => this.scene.start('ArenaCreate', { editId: tv.id }), {
        width: Math.min(160, w * 0.26), height: Math.max(34, 38 * s), color: PALETTE.trackLine, fontSize: Math.max(12, Math.round(14 * s)),
      });
    }

    // Join button (if open & not joined). Settled/ended → none.
    if (!tv.joined && (tv.status === 'running' || tv.status === 'upcoming')) {
      const label = tv.entryFee > 0 ? `${t('arena.join')} · ${tv.entryFee} 🪙` : `${t('arena.join')} · ${t('arena.free')}`;
      const canAfford = SaveManager.instance.coins >= tv.entryFee;
      new Button(this, cx, h * 0.29, label, () => this.join(tv), {
        width: Math.min(320, w * 0.6), height: Math.max(50, 56 * s), color: canAfford ? PALETTE.good : PALETTE.trackLine, fontSize: Math.max(15, Math.round(18 * s)),
      });
      if (!canAfford) this.add.text(cx, h * 0.35, t('arena.errInsufficient'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(13 * s)}px`, color: '#ff8f9c' }).setOrigin(0.5);
      this.standingsPanel(tv, w * 0.5, h * 0.4, w * 0.9, h * 0.52, s);
      return;
    }

    // Two columns: disciplines (left, selectable) + right panel (total standings, or the selected
    // event's standings + Play button). Re-rendered in place on selection.
    this.colTop = h * 0.28; this.colH = h - this.colTop - 16 * s;
    this.renderColumns();
  }

  /** (Re)draw both columns for the current selection. Left = events; right = total or per-event. */
  private renderColumns(): void {
    const { w, s } = viewport(this);
    if (!this.tv) return;
    this.colObjects.forEach((o) => o.destroy());
    this.colObjects = [];
    this.disciplinesPanel(this.tv, w * 0.06, this.colTop, w * 0.44, this.colH, s);
    if (this.selDisc) this.disciplineDetailPanel(this.tv, this.selDisc, w * 0.52, this.colTop, w * 0.42, this.colH, s);
    else this.standingsPanel(this.tv, w * 0.52, this.colTop, w * 0.42, this.colH, s);
  }

  private select(disc: string): void {
    AudioEngine.tap(0.35);
    this.selDisc = this.selDisc === disc ? null : disc;
    this.renderColumns();
  }

  private disciplinesPanel(tv: TournDetail, x: number, y: number, pw: number, ph: number, s: number): void {
    this.track(drawPanel(this, x + pw / 2, y + ph / 2, pw, ph, { radius: 14, fillAlpha: 0.6 }));
    this.track(this.add.text(x + pw / 2, y + 16 * s, t('arena.disciplines'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(13, Math.round(15 * s))}px`, color: '#cfd8f5', fontStyle: 'bold' }).setOrigin(0.5, 0));
    const top = y + 38 * s, bottom = y + ph - 12 * s;
    const rowH = Math.max(44, 50 * s), gap = 8 * s, stride = rowH + gap;
    const me = CloudSync.user?.sub;
    const container = this.track(this.add.container(0, top));
    this.maskContainer(container, x, top, pw, bottom - top);

    tv.disciplines.forEach((d, i) => {
      const ry = i * stride;
      const rx = x + 10 * s, rw = pw - 20 * s;
      const sel = this.selDisc === d;
      // Tap a row to SELECT the event (its standings + Play appear in the right panel). The whole
      // row is the tap target; the list zone below distinguishes tap from drag-scroll.
      const g = this.add.graphics();
      g.fillStyle(sel ? PALETTE.laneAccent : PALETTE.ink, sel ? 0.22 : 0.5).fillRoundedRect(rx, ry, rw, rowH, 12);
      g.lineStyle(2, sel ? PALETTE.laneAccent : PALETTE.cadence, sel ? 1 : 0.2).strokeRoundedRect(rx, ry, rw, rowH, 12);
      container.add(g);
      const nm = this.add.text(rx + 14 * s, ry + rowH * 0.34, disciplineLabel(d), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(13, Math.round(15 * s))}px`, color: '#f4f7ff', fontStyle: 'bold' }).setOrigin(0, 0.5);
      if (nm.width > rw * 0.7) nm.setScale((rw * 0.7) / nm.width);
      container.add(nm);
      // Sub-line: my result + my position out of how many have ALREADY played this event (X/Y) —
      // so you see at a glance who's done it. "— · N da giocare" until you've played it.
      const played = tv.byDiscipline[d]?.length ?? 0;
      const myRank = (tv.byDiscipline[d] ?? []).findIndex((r) => r.uid === me) + 1; // 0 if not present
      const my = tv.myResults[d];
      // Medal matches the actual position: gold/silver/bronze on the podium, a neutral ○ off it
      // (only possible with 4+ players who have played). No medal until you've played the event.
      const medal = myRank === 1 ? '🥇' : myRank === 2 ? '🥈' : myRank === 3 ? '🥉' : '○';
      const sub = my != null
        ? `${formatMetric(d, metricKindOf(d), my)}   ${medal} ${myRank}/${played}`
        : `—   (${played} ${t('arena.played')})`;
      container.add(this.add.text(rx + 14 * s, ry + rowH * 0.72, sub, {
        fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(11, Math.round(12 * s))}px`, color: my != null ? '#6ef0a0' : '#8893b8',
      }).setOrigin(0, 0.5));
      container.add(this.add.text(rx + rw - 12 * s, ry + rowH / 2, sel ? '▸' : '›', {
        fontFamily: 'system-ui', fontSize: `${Math.max(16, Math.round(20 * s))}px`, color: sel ? '#ffd24a' : '#59d2ff', fontStyle: 'bold',
      }).setOrigin(1, 0.5));
    });
    const totalH = tv.disciplines.length * stride;
    this.scrollList(container, x, top, pw, bottom - top, totalH, { stride, onTap: (i) => { if (tv.disciplines[i]) this.select(tv.disciplines[i]!); } });
  }

  /** Right panel when an event is selected: its standings + my result + Play + back-to-overall. */
  private disciplineDetailPanel(tv: TournDetail, disc: string, x: number, y: number, pw: number, ph: number, s: number): void {
    this.track(drawPanel(this, x + pw / 2, y + ph / 2, pw, ph, { radius: 14, fillAlpha: 0.6 }));
    // Back to overall standings.
    this.track(new Button(this, x + pw - 16 * s - Math.min(150, pw * 0.42) / 2, y + 18 * s, `↩ ${t('arena.standings')}`, () => this.select(disc), {
      width: Math.min(150, pw * 0.42), height: Math.max(30, 34 * s), color: PALETTE.trackLine, fontSize: Math.max(11, Math.round(13 * s)),
    }));
    this.track(this.add.text(x + 14 * s, y + 18 * s, disciplineLabel(disc), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(13, Math.round(16 * s))}px`, color: '#ffd24a', fontStyle: 'bold' }).setOrigin(0, 0.5));

    const playable = tv.joined && tv.status === 'running';
    const rows = tv.byDiscipline[disc] ?? [];
    const me = CloudSync.user?.sub;
    const kind = metricKindOf(disc);
    const top = y + 42 * s;
    const bottom = y + ph - (playable ? Math.max(58, 64 * s) : 12 * s); // leave room for Play button
    const rowH = Math.max(30, 34 * s);
    const container = this.track(this.add.container(0, top));
    this.maskContainer(container, x, top, pw, bottom - top);

    if (!rows.length) {
      this.track(this.add.text(x + pw / 2, top + 24 * s, t('arena.noResults'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(11, Math.round(12 * s))}px`, color: '#8893b8', align: 'center', wordWrap: { width: pw - 30 * s } }).setOrigin(0.5, 0));
    } else {
      rows.forEach((row, i) => {
        const ry = i * rowH;
        const rx = x + 10 * s, rw = pw - 20 * s;
        const mine = row.uid === me;
        if (mine) { const g = this.add.graphics(); g.fillStyle(PALETTE.good, 0.18).fillRoundedRect(rx, ry, rw, rowH - 3, 8); container.add(g); }
        const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`;
        container.add(this.add.text(rx + 8 * s, ry + rowH / 2, medal, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(14 * s))}px`, color: '#f4f7ff' }).setOrigin(0, 0.5));
        const nm = this.add.text(rx + 40 * s, ry + rowH / 2, mine ? `${row.name} (${t('arena.you')})` : row.name, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`, color: mine ? '#6ef0a0' : '#f4f7ff', fontStyle: mine ? 'bold' : 'normal' }).setOrigin(0, 0.5);
        if (nm.width > rw * 0.5) nm.setScale((rw * 0.5) / nm.width);
        container.add(nm);
        container.add(this.add.text(rx + rw - 8 * s, ry + rowH / 2, formatMetric(disc, kind, row.metric), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`, color: '#ffd24a', fontStyle: 'bold' }).setOrigin(1, 0.5));
      });
      this.scrollList(container, x, top, pw, bottom - top, rows.length * rowH);
    }

    if (playable) {
      this.track(new Button(this, x + pw / 2, y + ph - Math.max(30, 34 * s), `▶  ${t('arena.play')}`, () => this.play(tv, disc), {
        width: Math.min(280, pw * 0.8), height: Math.max(48, 52 * s), color: PALETTE.laneAccent, fontSize: Math.max(15, Math.round(18 * s)),
      }));
    }
  }

  private standingsPanel(tv: TournDetail, x: number, y: number, pw: number, ph: number, s: number): void {
    this.track(drawPanel(this, x + pw / 2, y + ph / 2, pw, ph, { radius: 14, fillAlpha: 0.6 }));
    this.track(this.add.text(x + pw / 2, y + 16 * s, t('arena.standings'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(13, Math.round(15 * s))}px`, color: '#cfd8f5', fontStyle: 'bold' }).setOrigin(0.5, 0));
    const me = CloudSync.user?.sub;
    const top = y + 38 * s, bottom = y + ph - 12 * s;
    const rowH = Math.max(30, 34 * s);
    const container = this.track(this.add.container(0, top));
    this.maskContainer(container, x, top, pw, bottom - top);

    if (!tv.standings.length) {
      this.track(this.add.text(x + pw / 2, top + 30 * s, t('arena.noResults'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(11, Math.round(12 * s))}px`, color: '#8893b8', align: 'center', wordWrap: { width: pw - 30 * s } }).setOrigin(0.5, 0));
      return;
    }
    tv.standings.forEach((row, i) => {
      const ry = i * rowH;
      const rx = x + 10 * s, rw = pw - 20 * s;
      const mine = row.uid === me;
      if (mine) { const g = this.add.graphics(); g.fillStyle(PALETTE.good, 0.18).fillRoundedRect(rx, ry, rw, rowH - 3, 8); container.add(g); }
      const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`;
      container.add(this.add.text(rx + 8 * s, ry + rowH / 2, `${medal}`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(14 * s))}px`, color: '#f4f7ff' }).setOrigin(0, 0.5));
      const nm = this.add.text(rx + 40 * s, ry + rowH / 2, mine ? `${row.name} (${t('arena.you')})` : row.name, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`, color: mine ? '#6ef0a0' : '#f4f7ff', fontStyle: mine ? 'bold' : 'normal' }).setOrigin(0, 0.5);
      if (nm.width > rw * 0.6) nm.setScale((rw * 0.6) / nm.width);
      container.add(nm);
      container.add(this.add.text(rx + rw - 8 * s, ry + rowH / 2, `${row.points} pt`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`, color: '#ffd24a', fontStyle: 'bold' }).setOrigin(1, 0.5));
    });
    this.scrollList(container, x, top, pw, bottom - top, tv.standings.length * rowH);
  }

  /**
   * Drag-to-scroll + tap-to-select over a masked list. A single zone covers the viewport: a small
   * pointer movement is treated as a TAP (→ onTap with the row index), a larger one as a drag
   * (→ scroll). This makes the whole row tappable on mobile and stops the scroll zone from eating
   * the touch meant for a button.
   */
  /** Geometry-mask a scroll container to a viewport rect, tracking the mask source for cleanup. */
  private maskContainer(container: Phaser.GameObjects.Container, x: number, top: number, w: number, h: number): void {
    const m = this.make.graphics({});
    m.fillRect(x, top, w, h);
    this.track(m);
    container.setMask(m.createGeometryMask());
  }

  private scrollList(
    container: Phaser.GameObjects.Container, x: number, top: number, vw: number, viewH: number, totalH: number,
    tap?: { stride: number; onTap: (index: number) => void },
  ): void {
    const scrollable = totalH > viewH;
    if (!scrollable && !tap) return; // nothing to scroll, nothing to tap
    const minY = top - Math.max(0, totalH - viewH), maxY = top;
    const zone = this.track(this.add.zone(x, top, vw, viewH).setOrigin(0, 0).setInteractive());
    let dragging = false, downY = 0, startY = 0, moved = 0;
    zone.on('pointerdown', (p: Phaser.Input.Pointer) => { dragging = true; downY = p.y; startY = container.y; moved = 0; });
    zone.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!dragging || !p.isDown) return;
      const dy = p.y - downY;
      if (Math.abs(dy) > moved) moved = Math.abs(dy);
      if (scrollable) container.y = Phaser.Math.Clamp(startY + dy, minY, maxY);
    });
    zone.on('pointerup', (p: Phaser.Input.Pointer) => {
      const wasTap = dragging && moved < 12;
      dragging = false;
      if (wasTap && tap) {
        const idx = Math.floor((p.y - container.y) / tap.stride);
        if (idx >= 0) tap.onTap(idx);
      }
    });
  }

  private play(tv: TournDetail, disciplineId: string): void {
    AudioEngine.tap(0.5);
    setArenaPlay({ tournamentId: tv.id, tournamentName: tv.name, disciplineId });
    this.scene.start('RosterSelect', { disciplineId });
  }

  private join(tv: TournDetail): void {
    if (this.busy) return;
    if (SaveManager.instance.coins < tv.entryFee) { AudioEngine.tap(0.2); this.cameras.main.shake(160, 0.005); return; }
    this.busy = true;
    AudioEngine.tap(0.5);
    void arenaJoin(tv.id).then((r) => {
      this.busy = false;
      if (!r.ok || !r.data) {
        const msg = r.error === 'full' ? t('arena.errFull') : r.error === 'closed' ? t('arena.errClosed') : r.error === 'insufficient' ? t('arena.errInsufficient') : t('arena.offline');
        this.toast(msg);
        return;
      }
      AudioEngine.chime();
      SaveManager.instance.setServerCoins(r.data.coins);
      this.scene.restart({ tournamentId: this.tournamentId });
    });
  }

  private toast(msg: string): void {
    const { cx, h, s } = viewport(this);
    const txt = this.add.text(cx, h * 0.9, msg, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(15 * s)}px`, color: '#ff8f9c', fontStyle: 'bold', backgroundColor: '#0b1020cc', padding: { x: 12, y: 6 } }).setOrigin(0.5).setDepth(60);
    this.tweens.add({ targets: txt, alpha: 0, delay: 1800, duration: 600, onComplete: () => txt.destroy() });
  }

  private coinChip(rightX: number, y: number, s: number): void {
    const fs = Math.round(Math.max(17, 19 * s));
    const tx = this.add.text(0, 0, `🪙 ${SaveManager.instance.coins}`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, color: '#ffffff', fontStyle: 'bold' }).setShadow(0, 2, '#000000cc', 3);
    const padX = 14 * s, ww = tx.width + padX * 2, hh = tx.height + 12 * s, bx = rightX - ww;
    this.add.graphics().setDepth(18)
      .fillStyle(PALETTE.ink, 0.78).fillRoundedRect(bx, y, ww, hh, hh / 2)
      .lineStyle(2, PALETTE.cadence, 0.6).strokeRoundedRect(bx, y, ww, hh, hh / 2);
    tx.setPosition(bx + padX, y + hh / 2).setOrigin(0, 0.5).setDepth(19);
  }
}
